import React from 'react';
import { useSelector } from 'react-redux';
import { Pane, SymbolCircleIcon, Text } from 'evergreen-ui';
import { datasourceStatusDataReceivedSelector } from 'Features/App/Dictionaries/dictionariesSlice';
import {
  useLazyGetDatasourceProgramsReportCountQuery,
} from 'Services/api/v2/datasourceProgramsApi';
import SuspenseSpinner from 'Utils/SuspenseSpinner';
import { createUseStyles } from 'react-jss';
import { timeZoneSelector } from 'Features/App/appSlice';
import { UTCtoTimeZone } from 'Utils/UTCConverter';
import { format, set } from 'date-fns';
import { DatasourceIdProps } from '../utils';
import StatusIcon from './StatusIcon';
import {
  endDate,
  startDate,
  timeFormat,
  StatusDataType,
  getDangerColors,
  getSuccessColors,
  getWarningColors,
} from './utils';

const useStyle = createUseStyles({
  dataReceived: {
    justifyContent: 'center',
    minWidth: 'max-content',
    alignItems: 'center',
  },
});

export function DataReceivedStatus({ datasourceId }: DatasourceIdProps) {
  const selectedZone = useSelector(timeZoneSelector);
  const status = useSelector(datasourceStatusDataReceivedSelector(datasourceId));

  const { text, intent, time }: StatusDataType = React.useMemo(() => {
    const { dataReceived, dateTimeUpdate } = status || {};

    switch (dataReceived) {
      case 'ALL':
        return {
          time: UTCtoTimeZone(dateTimeUpdate, selectedZone.selectedUserTimeZone),
          text: 'Все программы наблюдения поступили в срок',
          intent: 'success',
        };
      case 'PART':
        return {
          time: UTCtoTimeZone(dateTimeUpdate, selectedZone.selectedUserTimeZone),
          text: 'Часть программ наблюдения не поступили в срок',
          intent: 'warning',
        };
      case 'NONE':
        return {
          time: UTCtoTimeZone(dateTimeUpdate, selectedZone.selectedUserTimeZone),
          text: 'Программы наблюдения не поступили в срок',
          intent: 'danger',
        };
      default:
        return {
          text: 'Поступление программ наблюдения: Неизвестно',
          intent: 'none',
          time: '',
        };
    }
  }, [selectedZone.selectedUserTimeZone, status]);

  return (
    <StatusIcon
      dataReceivedText={text}
      time={time}
      intent={intent}
      icon={SymbolCircleIcon}
      popover={
        <DialogBoxItem datasourceId={datasourceId} />
      }
    />
  );
}

export default DataReceivedStatus;

type DialogBoxItemProps = {
  datasourceId: number,
}

function DialogBoxItem({ datasourceId }: DialogBoxItemProps) {
  const selectedZone = useSelector(timeZoneSelector);
  const status = useSelector(datasourceStatusDataReceivedSelector(datasourceId));

  const classes = useStyle();
  const { dataReceived } = status || {};

  const [receivedReportCountRequest,
    { currentData: dataRequestReceived = {}, isLoading, isFetching }] = useLazyGetDatasourceProgramsReportCountQuery();

  const { deliveredTimeCount, notDeliveredTimeCount } = dataRequestReceived;
  const totalDeliveredCount = deliveredTimeCount + notDeliveredTimeCount;

  const successColor = getSuccessColors().MAIN;
  const warningColor = getWarningColors().MAIN;
  const dangerColor = getDangerColors().MAIN;

  React.useEffect(() => {
    if (dataReceived) {
      receivedReportCountRequest({ datasourceId, startDate, endDate });
    }
  }, [dataReceived, datasourceId, receivedReportCountRequest]);

  const colorized = () => {
    if (deliveredTimeCount === totalDeliveredCount) return successColor;
    if (deliveredTimeCount < totalDeliveredCount && deliveredTimeCount > 0) return warningColor;
    if (deliveredTimeCount === 0) return dangerColor;
    return null;
  };

  const date = new Date();

  const getCurrentDate = format(set(new Date(), {
    hours: date
      .getUTCHours(),
    minutes: date
      .getUTCMinutes(),
    seconds: date
      .getUTCSeconds(),
  }), timeFormat);

  if (isLoading || isFetching) return <SuspenseSpinner size={20} />;
  if (!dataReceived) return null;
  return (
    <Pane className={classes.dataReceived}>
      {endDate && (<Text>{`${UTCtoTimeZone(getCurrentDate, selectedZone.selectedUserTimeZone)} UTC${selectedZone.selectedUserTimeZone} - `}</Text>)}
      <Text>Поступило </Text>
      <Text color={colorized()}>{`${deliveredTimeCount} из ${totalDeliveredCount} `}</Text>
      <Text>сообщений за 24 часа.</Text>
    </Pane>
  );
}
